import type { BusinessSettings, CarWashState } from '@/types'

import { DEFAULT_TEMPLATES } from '@/constants/carwash'

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

const asArray = <T>(value: unknown): T[] => Array.isArray(value) ? value as T[] : []

/*
 * Reads whatever was saved on the device and returns a complete state, or
 * throws when the saved data is not a car wash state at all.
 */
export const migrateState = (raw: unknown): CarWashState => {
    if (!isObject(raw) || raw.version !== 1 || !isObject(raw.settings)) throw new Error('Saved data is not readable.')

    const settings = raw.settings as Partial<BusinessSettings>
    const templates = isObject(settings.templates) ? settings.templates as Partial<BusinessSettings['templates']> : {}

    return {
        version: 1,
        settings: {
            name: settings.name ?? 'My Car Wash',
            logo: settings.logo ?? null,
            address: settings.address ?? '',
            phone: settings.phone ?? '',
            taxId: settings.taxId ?? '',
            currency: settings.currency ?? 'INR',
            countryCode: settings.countryCode ?? '91',
            invoicePrefix: settings.invoicePrefix ?? 'INV-',
            nextInvoiceNumber: typeof settings.nextInvoiceNumber === 'number' ? settings.nextInvoiceNumber : 1,
            invoiceFooter: settings.invoiceFooter ?? '',
            defaultPaymentMethod: settings.defaultPaymentMethod ?? 'cash',
            templates: {
                received: templates.received ?? DEFAULT_TEMPLATES.received,
                completed: templates.completed ?? DEFAULT_TEMPLATES.completed,
                payment: templates.payment ?? DEFAULT_TEMPLATES.payment
            },
            updatedAt: settings.updatedAt ?? new Date().toISOString()
        },
        services: asArray(raw.services),
        staff: asArray(raw.staff),
        customers: asArray(raw.customers),
        vehicles: asArray(raw.vehicles),
        jobs: asArray(raw.jobs),
        invoices: asArray(raw.invoices),
        payments: asArray(raw.payments),
        messages: asArray(raw.messages),
        nextJobNumber: typeof raw.nextJobNumber === 'number' ? raw.nextJobNumber : 1,
        createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date().toISOString()
    }
}
